import type { DocumentPickerAsset } from "expo-document-picker";

import type {
  DeliverableFileType,
  SubmissionStatus,
  TeamDeliverableStatusRow,
  TeamSubmissionRow,
} from "../types/database";
import { supabase } from "./supabase";

import { listMyTeam } from "./teams";

type RawSubmissionJoin = {
  id: string;
  deliverable_id: string;
  team_id: string;
  competition_id: string;
  submitted_by: string;
  file_url: string;
  file_name: string;
  file_size_bytes: number | null;
  status: SubmissionStatus;
  submitted_at: string;
  updated_at: string;
  deliverables: { sequence: number; name: string; due_at: string | null } | { sequence: number; name: string; due_at: string | null }[] | null;
};

function unwrapDeliverable(
  d: RawSubmissionJoin["deliverables"]
): { sequence: number; name: string; due_at: string | null } | null {
  if (d == null) return null;
  return Array.isArray(d) ? (d[0] ?? null) : d;
}

export async function listMyTeamSubmissions(
  competitionId: string,
  teamId?: string | null
): Promise<TeamSubmissionRow[]> {
  const tid = teamId ?? (await listMyTeam(competitionId))?.team.id;
  if (!tid) return [];

  const { data, error } = await supabase
    .from("submissions")
    .select("*, deliverables(sequence, name, due_at)")
    .eq("team_id", tid)
    .eq("competition_id", competitionId);

  if (error || !data) return [];

  return (data as RawSubmissionJoin[]).map((row) => {
    const del = unwrapDeliverable(row.deliverables);
    return {
      id: row.id,
      deliverable_id: row.deliverable_id,
      team_id: row.team_id,
      competition_id: row.competition_id,
      submitted_by: row.submitted_by,
      file_url: row.file_url,
      file_name: row.file_name,
      file_size_bytes: row.file_size_bytes,
      status: row.status,
      submitted_at: row.submitted_at,
      updated_at: row.updated_at,
      deliverable_sequence: del?.sequence ?? 0,
      deliverable_name: del?.name ?? "",
      deliverable_due_at: del?.due_at ?? null,
    };
  });
}

export function documentPickerTypesForFileType(fileType: DeliverableFileType): string | string[] {
  switch (fileType) {
    case "excel":
      return [
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        "application/vnd.ms-excel",
      ];
    case "pdf":
      return "application/pdf";
    case "powerpoint":
      return "application/vnd.openxmlformats-officedocument.presentationml.presentation";
    case "word":
      return "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
    case "csv":
      return "text/csv";
    case "zip":
      return "application/zip";
    case "image":
      return "image/*";
    case "video":
      return "video/*";
    case "any":
    default:
      return "*/*";
  }
}

const mimeForDeliverableStorage: Record<DeliverableFileType, string | undefined> = {
  pdf: "application/pdf",
  excel: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  powerpoint: "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  word: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  csv: "text/csv",
  zip: "application/zip",
  image: undefined,
  video: undefined,
  any: undefined,
};

function storagePath(teamId: string, deliverableId: string, fileName: string): string {
  const safe = fileName.replace(/[^\w.\-()+]/g, "_");
  return `${teamId}/${deliverableId}/${safe}`;
}

export async function readUploadBody(asset: DocumentPickerAsset): Promise<{ body: Blob | File; contentType: string }> {
  const contentType = asset.mimeType || "application/octet-stream";
  if (asset.file) {
    return { body: asset.file, contentType: asset.file.type || contentType };
  }
  const res = await fetch(asset.uri);
  const blob = await res.blob();
  return { body: blob, contentType: blob.type || contentType };
}

export async function uploadSubmission(params: {
  deliverableId: string;
  teamId: string;
  competitionId: string;
  deliverableFileType: DeliverableFileType;
  dueAtIso: string | null;
  file: DocumentPickerAsset;
}): Promise<{ error: Error | null }> {
  const { data: userData, error: userError } = await supabase.auth.getUser();
  if (userError || !userData.user) {
    return { error: new Error(userError?.message ?? "Not signed in") };
  }

  const path = storagePath(params.teamId, params.deliverableId, params.file.name);
  const { body, contentType: detectedType } = await readUploadBody(params.file);
  const fileTypeMime = mimeForDeliverableStorage[params.deliverableFileType];
  const contentType =
    fileTypeMime ?? params.file.mimeType ?? params.file.file?.type ?? detectedType;

  const { error: upErr } = await supabase.storage.from("submissions").upload(path, body, {
    contentType,
    upsert: true,
  });
  if (upErr) return { error: new Error(upErr.message) };

  const now = new Date();
  const due = params.dueAtIso ? new Date(params.dueAtIso) : null;
  const status: SubmissionStatus =
    due && !Number.isNaN(due.getTime()) && now.getTime() > due.getTime() ? "late" : "submitted";

  const row = {
    deliverable_id: params.deliverableId,
    team_id: params.teamId,
    competition_id: params.competitionId,
    submitted_by: userData.user.id,
    file_url: path,
    file_name: params.file.name,
    file_size_bytes: params.file.size ?? null,
    status,
    submitted_at: now.toISOString(),
    updated_at: now.toISOString(),
  };

  const { error: dbErr } = await supabase.from("submissions").upsert(row, {
    onConflict: "deliverable_id,team_id",
  });
  if (dbErr) return { error: new Error(dbErr.message) };
  void import("./analytics").then(({ track }) =>
    track("submission_submitted", {
      deliverable_id: params.deliverableId,
      team_id: params.teamId,
      competition_id: params.competitionId,
      status,
      file_size_bytes: params.file.size ?? null,
    })
  );
  return { error: null };
}

export async function getSubmissionDownloadUrl(filePath: string): Promise<{ url: string | null; error: Error | null }> {
  const { data, error } = await supabase.storage.from("submissions").createSignedUrl(filePath, 3600);
  if (error) return { url: null, error: new Error(error.message) };
  return { url: data?.signedUrl ?? null, error: null };
}

export async function getTeamDeliverableStatuses(teamId: string): Promise<TeamDeliverableStatusRow[]> {
  const { data, error } = await supabase.rpc("get_team_deliverable_statuses", {
    p_team_id: teamId,
  });
  if (error) throw new Error(error.message);
  return (data ?? []) as TeamDeliverableStatusRow[];
}
